<template>
    <transition-group name="modal" v-if="isShown" ref="container">
        <div 
            class="vuedal modal" 
            tabindex="-1" 
            role="dialog" 
            ref="modal"
            @mousedown.self="onBlur"
            :key="scopeId"
        >
            <div v-bind:class="sizeClasses[size]" class="modal-dialog" role="document">
                <div class="modal-content" :style="modalStyle">
                   
                    <div class="loading-wrapper" v-if="loading">
                        <i class="fa fa-spinner fa-pulse fa-3x fa-fw"></i>
                        <span class="sr-only">Loading...</span>
                        <div class="loading-overlay"></div>
                    </div>

                    <div class="modal-header">
                        <slot name="modal-title"></slot>
                        
                        <button type="button" 
                                class="close"  
                                aria-label="Close"
                                @click="hide"
                        ><span aria-hidden="true">&times;</span></button>
                    </div>
                    <div class="modal-body">
                        <slot name="modal-body">
                            
                        </slot>
                    </div>
                    <div class="modal-footer">
                        <slot name="modal-footer">
                        </slot>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="rootModal"
            class="modal-backdrop show"
            @click="onBlur"
            :key="`${scopeId}-backdrop`"
        ></div>
    </transition-group>
</template>

<script>
    //Actually a VueDal

    //Based on:
    //https://vuejs.org/v2/examples/modal.html
    
    import $ from 'jquery';
    import _ from 'lodash';
    
    let _modalId = 0;
    function generateModalScopeId(){
        const id = _modalId++;
        return `data-vuedal-${id}`;
    }

    const sizes = {
        small: 'small',
        medium: 'medium',
        large: 'large',
        extraLarge: 'extraLarge',
        
        custom: 'custom'
    };
    
    const sizeClasses = {
        small: 'modal-sm',
        large: 'modal-lg',
        extraLarge: 'modal-xl',

        medium: '',
        custom: ''
    };
    
    export {
        sizes
    }
    
    export default {
        name: "modal",
        
        props: {
            size: {
                type: String,
                default: sizes.medium,
                validate: v => {
                    return Object.values(sizes).indexOf(v) !== -1
                }
            },
            customSize: {
                type: Number
            },
            options: {
                type: Object,
                default: () => {
                    return {};
                }
            },
            loading: false,
            showOnMount: {
                type: Boolean,
                default: false,
                required: false
            }
        },
        
        computed: {
            opts() {
                return _.merge({}, this.defaultOptions, this.options);
            },
            modalStyle() {
                return this.opts.minHeight ? `min-height: ${this.opts.minHeight}px` : '';
            }
        },
        
        data: () => ({
            sizeClasses: sizeClasses,
            isShown: false,
            
            rootModal: true,
            defaultOptions: {
                close: {    
                    onEsc: true,
                    onBlur: true
                }
            },
            scopeId: generateModalScopeId()
        }),
        
        mounted() {
            document.addEventListener('keyup', this.closeListener);

            if(this.showOnMount){
                this.show();
            }
        },
        
        beforeDestroy() {
            document.removeEventListener('keyup', this.closeListener);

            //Prevent our modal from becoming a zombie modal by ensuring proper disposal prior to deletion
            const modalElement = this.getModalElement();

            if(modalElement){
                modalElement.remove();
            }

            this.cleanup();
        },
        
        methods: {
            closeListener(event){
                if(event.key === 'Escape' && this.isShown && this.opts.close.onEsc) {
                    this.hide();
                }
            },
            
            //Using a computed might cause de-sync between active value/cached value
            //this causes the value to not refer to the current this.$refs.container.$el value
            //anymore on a second invocation of the 'show' method
            getModalElement(){
                return this.$refs.container && this.$refs.container.$el;
            },
            cleanup(){
                //Remove all overlays added to parent modals
                $(`.modal-content .modal-overlay[${this.scopeId}]`).remove();
                
                if(this.rootModal) {
                    document.body.classList.remove("modal-open");
                }
            },
            show(){
                const vm = this;
                const openModals = $('.modal-content');

                if(openModals.length > 0) {
                    vm.rootModal = false;

                    //Add an overlay to prevent interaction with our parent modal
                    openModals.append(`<div ${vm.scopeId} class="modal-overlay"></div>`);
                }

                vm.$emit('show');
                vm.isShown = true;

                //Our 'transition' container element will only be available once it's actually shown
                //We'll need to move our modal to the body
                //this is to prevent overlap with other elements

                vm.$nextTick(() => {
                    const el = vm.getModalElement();
                    
                    document.body.appendChild(el);
                    
                    if(vm.rootModal) {
                        document.body.classList.add("modal-open");
                    }
                });

                vm.$emit('shown');
            },
            onBlur(){
                const vm = this;
                
                if(vm.opts.close.onBlur !== false) {
                    vm.hide();
                }
            },
            async hide(){
                const vm = this;
                
                //Allow cancelling hide via native-like API
                let defaultPrevented = false;
                
                //Allow stalling (eg. by a prompt) until continuing
                let continueAfter = () => Promise.resolve();
                
                const evt = {
                    preventDefault: () => defaultPrevented = true,
                    continueAfter: callback => continueAfter = callback,
                    get defaultPrevented() { return defaultPrevented; }
                }

                vm.$emit('hide', evt);

                await continueAfter();
                
                if(evt.defaultPrevented){
                    return;
                }
                
                vm.isShown = false;

                this.cleanup();
                
                vm.$emit('hidden');
            }
        }
    }
</script>

<style lang="scss" src="./Styles/modal.scss">

</style>
